import React, { useEffect, useState } from "react";
import {
  Alert,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  TreeSelect,
  Typography,
  notification,
  AutoComplete,
} from "antd";
import { InputWrapper, SelectWrapper } from "./InviteDriver.styles";
import { CustomTagProps } from "rc-select/lib/interface/generator";
import { useRolesIndex } from "hooks/api/common/roles";
import { Role } from "interfaces/common/role";
import { startCase, upperCase, uniq } from "lodash-es";
import { useMutation } from "react-query";
import { inviteDrivers } from "api/accounts/user";
import { useServiceArea } from "hooks/api/common/useServiceArea";
import { useFleetOperators } from "hooks/api/common/useFleetOperators";

interface InviteDriverProps {
  visible: boolean;
  closeModal: () => void;
  organizationID: string;
}

const { Text } = Typography;

const InviteDriver = ({ visible, closeModal, organizationID }: InviteDriverProps) => {
  const [fleetOperator, setFleetOperator] = useState("");
  const [roleID, setRoleID] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [selectedServiceAreas, setSelectedServiceAreas] = useState<string[]>([]);
  const [selectedDspType, setSelectedDspType] = useState<string>("");
  const { fetchRolesIndex, rolesIndex } = useRolesIndex();
  const areAllEmailsValid = emails.every((email) => validateEmail(email));
  const isSubmitButtonDisabled =
    !areAllEmailsValid ||
    emails.length === 0 ||
    fleetOperator === "" ||
    !roleID ||
    !selectedDspType ||
    selectedServiceAreas.length === 0;
  const { serviceArea } = useServiceArea();
  const { data: fleetOperatorList } = useFleetOperators();

  const { mutate: sendInvites, isLoading: isSendInviteLoading } = useMutation(inviteDrivers, {
    onSuccess() {
      notification.success({
        message: "Request received.",
        description: "Invitations will be sent shortly.",
        duration: 20,
      });
      resetForm();
      closeModal();
    },
    onError(e: Error | string) {
      notification.error({
        message: "Error:",
        description: typeof e === "string" ? e : e.message,
        duration: 20,
      });
    },
  });

  const resetForm = () => {
    setFleetOperator("");
    setRoleID("");
    setSelectedDspType("");
    setSelectedServiceAreas([]);
    setEmails([]);
  };

  const handleSumbit = () => {
    const body = {
      role_id: roleID,
      fleet_operator: fleetOperator,
      emails: emails,
      service_areas: selectedServiceAreas,
      driver_type: selectedDspType,
    };
    sendInvites(body);
  };

  const fetchRolesAndPrefillForm = async () => {
    const roles = (await fetchRolesIndex({ organization_id: organizationID })) as Role[];

    if (!roles || roles.length === 0) return;

    const dspDriverRole = roles?.find((role) => role.name.toLowerCase().includes("dsp"));

    setRoleID(dspDriverRole?.id || "");
  };

  useEffect(() => {
    fetchRolesAndPrefillForm();
    // eslint-disable-next-line
  }, []);

  const renderRoleDropdown = () => {
    const availableRoles = rolesIndex?.data || [];

    const options = availableRoles.map((role: Role) => {
      const id = role.id;
      const name = role.name;
      return (
        <Select.Option key={id} value={id}>
          {startCase(name)}
        </Select.Option>
      );
    });

    return (
      <SelectWrapper>
        <Select value={roleID || "Select Role..."} onChange={(value) => setRoleID(value)}>
          {options}
        </Select>
      </SelectWrapper>
    );
  };

  const renderServiceAreaDropdown = () => {
    if (!serviceArea.data) return null;
    const options = serviceArea.data.map((serviceArea) => {
      return { value: serviceArea.name, title: upperCase(serviceArea.name) };
    });

    return (
      <SelectWrapper>
        <TreeSelect
          style={{ width: "100%" }}
          value={selectedServiceAreas}
          dropdownStyle={{ maxHeight: 400, overflow: "auto", paddingLeft: "0px" }}
          placeholder="Please select"
          treeCheckable
          multiple
          showSearch
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          onChange={(value) => setSelectedServiceAreas(value)}
          treeData={options}
        />
      </SelectWrapper>
    );
  };

  const renderDspTypeDropdown = () => {
    const dspTypes = ["fleet_operator", "independent_operator"];

    const options = dspTypes.map((type: string) => {
      return (
        <Select.Option key={type} value={type}>
          {startCase(type)}
        </Select.Option>
      );
    });

    return (
      <SelectWrapper>
        <Select value={selectedDspType} onChange={(value) => setSelectedDspType(value)} placeholder="Please select">
          {options}
        </Select>
      </SelectWrapper>
    );
  };

  const renderCompanyNameDropdown = () => {
    const options = uniq(fleetOperatorList).map((name) => {
      return { value: name };
    });

    return (
      <AutoComplete
        style={{ width: "100%" }}
        options={options}
        value={fleetOperator}
        onSearch={(value) => setFleetOperator(value)}
        onSelect={(value) => setFleetOperator(value)}
        filterOption={(inputValue, option) => option?.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1}
      />
    );
  };

  return (
    <Modal
      title="Invite Drivers"
      okText={"Invite"}
      visible={visible}
      confirmLoading={isSendInviteLoading}
      onOk={handleSumbit}
      onCancel={() => {
        resetForm();
        closeModal();
      }}
      okButtonProps={{ disabled: isSubmitButtonDisabled }}
    >
      <Spin spinning={serviceArea.loading}>
        <Space style={{ display: "flex" }} direction="vertical" size="middle">
          <Row>
            <Col style={{ width: "100%" }}>
              <Text>Company Name</Text>
              <InputWrapper>{renderCompanyNameDropdown()}</InputWrapper>
            </Col>
          </Row>
          <Row>
            <Col style={{ width: "100%" }}>
              <Text>Service Area</Text>
              {renderServiceAreaDropdown()}
            </Col>
          </Row>
          <Row>
            <Col style={{ width: "100%" }}>
              <Text>Role</Text>
              {renderRoleDropdown()}
            </Col>
          </Row>
          <Row>
            <Col style={{ width: "100%" }}>
              <Text>Driver Type</Text>
              {renderDspTypeDropdown()}
            </Col>
          </Row>
          <Row>
            <Col style={{ width: "100%" }}>
              <Text>Driver Email</Text>
              <InputWrapper>
                <Select
                  mode="tags"
                  value={emails}
                  tagRender={tagRender}
                  style={{ width: "100%" }}
                  onChange={(values) => setEmails(values)}
                  tokenSeparators={[",", " "]}
                  dropdownStyle={{ display: "none" }}
                  placeholder={"email, comma separated"}
                  allowClear
                />
              </InputWrapper>
            </Col>
          </Row>
          {!areAllEmailsValid && (
            <Row>
              <Col style={{ width: "100%" }}>
                <Alert message={"All emails added must be valid."} type="error" banner />
              </Col>
            </Row>
          )}
        </Space>
      </Spin>
    </Modal>
  );
};

const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

const tagRender = (props: CustomTagProps) => {
  const { label, value, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const isValidEmail = typeof value === "string" && validateEmail(value);
  return (
    <Tag
      color={isValidEmail ? "green" : "red"}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 5, marginTop: 5, maxWidth: "100%", overflow: "hidden" }}
    >
      {label}
    </Tag>
  );
};

export default InviteDriver;
